/**
 * CENTERED NAVIGATION LAYOUT ENHANCEMENTS
 * Handles active states and interactions for centered nav layout
 */

if (typeof CenteredNavHandler !== 'function') {

  class CenteredNavHandler {

    constructor() {
      this.init();
    }

    init() {
      // Check if we're using the centered nav layout
      const header = document.querySelector('.header--centered_nav');
      if (!header) return;

      // Only run on desktop
      if (window.innerWidth < 1024) return;

      const navCenter = header.querySelector('.header__nav-center');
      if (!navCenter) return;

      // Setup dropdown functionality
      this.setupDropdowns(navCenter);

      // Keep submenu open when one of its children is active
      this.keepActiveSubmenuOpen(navCenter);

      // Enhanced keyboard navigation
      this.setupKeyboardNavigation(navCenter);

      // Handle window resize
      this.handleResize();

      // Close all submenus on navigation
      this.handleNavigation();
    }

    /**
     * Handle navigation events to close submenus and remove active states
     */
    handleNavigation() {
      // Close submenus when clicking on any nav link
      document.addEventListener('click', (e) => {
        const clickedLink = e.target.closest('.nav-submenu a, .nav-link');
        if (clickedLink) {
          const nav = document.querySelector('.header--centered_nav .header__nav-center');
          if (nav) {
            // Close all submenus
            nav.querySelectorAll('.has-submenu').forEach(item => {
              item.classList.remove('submenu-active');
              const submenu = item.querySelector('.nav-submenu');
              if (submenu) {
                submenu.classList.remove('is-active');
              }
              const title = item.querySelector('.nav-title');
              if (title) {
                title.setAttribute('aria-expanded', 'false');
              }
            });
          }
        }
      });

      // Also remove active states on page load/navigation
      window.addEventListener('pageshow', () => {
        const nav = document.querySelector('.header--centered_nav .header__nav-center');
        if (nav) {
          nav.querySelectorAll('.has-submenu').forEach(item => {
            item.classList.remove('submenu-active');
            const submenu = item.querySelector('.nav-submenu');
            if (submenu) {
              submenu.classList.remove('is-active');
            }
            const title = item.querySelector('.nav-title');
            if (title) {
              title.setAttribute('aria-expanded', 'false');
            }
          });
        }
      });
    }

    /**
     * Keep submenus open when one of their children is the active link
     */
    keepActiveSubmenuOpen(nav) {
      const activeChildLinks = nav.querySelectorAll('.nav-submenu a.selected');

      activeChildLinks.forEach(selectedItem => {
        const parentSubmenu = selectedItem.closest('.has-submenu');
        if (!parentSubmenu) return;

        parentSubmenu.classList.add('submenu-active');
        
        // Add is-active class to submenu (REQUIRED for visibility)
        const submenu = parentSubmenu.querySelector('.nav-submenu');
        if (submenu) {
          submenu.classList.add('is-active');
        }

        const title = parentSubmenu.querySelector('.nav-title');
        if (title) {
          title.setAttribute('aria-expanded', 'true');
        }
      });
    }

    /**
     * Setup dropdown functionality for menu items with children
     * Using the EXACT same pattern as default sidebar navigation
     */
    setupDropdowns(nav) {
      // Use exact same selector pattern as default sidebar
      document.querySelectorAll('.header__nav-center .has-submenu > .submenu-title').forEach(elm => {
        elm.addEventListener('click', e => {
          const listItem = e.currentTarget.closest('.has-submenu');
          const submenu = listItem.querySelector('.nav-submenu');
          const isExpanded = e.currentTarget.getAttribute('aria-expanded') === 'true';
          
          // Close all other open submenus (exact same as default)
          document.querySelectorAll('.header__nav-center .has-submenu > .submenu-title[aria-expanded="true"]').forEach(openMenu => {
            if (openMenu !== e.currentTarget) {
              openMenu.setAttribute('aria-expanded', 'false');
              openMenu.closest('.has-submenu').classList.remove('submenu-active');
              openMenu.closest('.has-submenu').querySelector('.nav-submenu').classList.remove('is-active');
            }
          });

          // Toggle this submenu (exact same as default)
          e.currentTarget.setAttribute('aria-expanded', !isExpanded);
          listItem.classList.toggle('submenu-active', !isExpanded);
          submenu.classList.toggle('is-active', !isExpanded);
        });
      });

      // Keep submenu open if it contains the active item (exact same as default)
      document.querySelectorAll('.header__nav-center .nav-submenu a.selected').forEach(selectedItem => {
        const parentSubmenu = selectedItem.closest('.has-submenu');
        if (parentSubmenu) {
          const submenuTitle = parentSubmenu.querySelector('.submenu-title');
          submenuTitle.setAttribute('aria-expanded', 'true');
          parentSubmenu.classList.add('submenu-active');
          parentSubmenu.querySelector('.nav-submenu').classList.add('is-active');
        }
      });

      // Close dropdowns when clicking outside
      document.addEventListener('click', (e) => {
        if (!e.target.closest('.header__nav-center .has-submenu')) {
          document.querySelectorAll('.header__nav-center .has-submenu > .submenu-title[aria-expanded="true"]').forEach(link => {
            link.setAttribute('aria-expanded', 'false');
            link.closest('.has-submenu').classList.remove('submenu-active');
            link.closest('.has-submenu').querySelector('.nav-submenu').classList.remove('is-active');
          });
        }
      });

      // Close dropdowns on Escape key (accessibility)
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          document.querySelectorAll('.header__nav-center .has-submenu > .submenu-title[aria-expanded="true"]').forEach(link => {
            link.setAttribute('aria-expanded', 'false');
            link.closest('.has-submenu').classList.remove('submenu-active');
            link.closest('.has-submenu').querySelector('.nav-submenu').classList.remove('is-active');
          });
        }
      });
    }

    /**
     * Enhanced keyboard navigation for nav links
     */
    setupKeyboardNavigation(nav) {
      const links = nav.querySelectorAll('.nav-link');

      links.forEach((link, index) => {
        link.addEventListener('keydown', (e) => {
          // Arrow key navigation
          if (e.key === 'ArrowRight') {
            e.preventDefault();
            const nextLink = links[index + 1] || links[0];
            nextLink.focus();
          } else if (e.key === 'ArrowLeft') {
            e.preventDefault();
            const prevLink = links[index - 1] || links[links.length - 1];
            prevLink.focus();
          }
        });
      });
    }

    /**
     * Handle window resize - switch between layouts
     */
    handleResize() {
      let resizeTimer;

      window.addEventListener('resize', () => {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(() => {
          const header = document.querySelector('.header--centered_nav');
          if (!header) return;

          const navCenter = header.querySelector('.header__nav-center');
          const menuButton = header.querySelector('.menu-opener');

          if (window.innerWidth >= 1024) {
            // Desktop: Show centered nav, hide hamburger
            if (navCenter) navCenter.style.display = 'flex';
            if (menuButton) menuButton.style.display = 'none';
          } else {
            // Mobile: Hide centered nav, show hamburger
            if (navCenter) navCenter.style.display = 'none';
            if (menuButton) menuButton.style.display = 'flex';
          }
        }, 150);
      });
    }
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      new CenteredNavHandler();
    });
  } else {
    new CenteredNavHandler();
  }
}
